import { useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import uniqueId from 'lodash/uniqueId';
import isEqual from 'lodash/isEqual';
import { Map } from 'immutable';
import { updateRow } from '../../store/data/actions';
import ValidationGroupContext from '../ValidationGroupContext';
import DataPathContext from '../DataPathContext';
const ensureValidationGroupExists = (row, validationGroupPath)=>{
    if (!row.hasIn(validationGroupPath)) {
        return row.setIn(validationGroupPath, Map());
    }
    return row;
};
const setItemAction = (row, validationGroupPath, id, isDirty, errors)=>{
    return row.updateIn(validationGroupPath, (g)=>{
        const group = g || undefined;
        return group && group.set(id, {
            $isDirty: isDirty,
            $errors: errors
        });
    });
};
const removeItemFromGroup = (row, validationGroupPath, id)=>row.updateIn(validationGroupPath, (g)=>{
        const group = g || undefined;
        return group && group.delete(id);
    });
export const useCustomValidationGroupPath = (customGroup)=>{
    const ctxDataPath = useContext(DataPathContext);
    const validationGroup = useContext(ValidationGroupContext);
    return useMemo(()=>{
        if (customGroup) {
            if (typeof customGroup === 'string') {
                return {
                    path: ctxDataPath,
                    name: customGroup
                };
            } else {
                return customGroup;
            }
        }
        return validationGroup;
    }, [
        customGroup,
        validationGroup,
        ctxDataPath
    ]);
};
const executeActions = (validationGroup, dispatch, actions)=>{
    if (validationGroup) {
        const validationGroupPath = [
            '$validationGroups',
            validationGroup.name
        ];
        dispatch(updateRow(validationGroup.path.path, (row)=>actions.reduce((r, fn)=>fn(r, validationGroupPath), row), {
            skipHistory: true,
            detachedId: validationGroup.path.detachedId ?? undefined
        }));
    }
};
export const useValidationGroupable = (isDirty, errors, { idPrefix, customGroup })=>{
    const id = useMemo(()=>uniqueId(idPrefix), [
        idPrefix
    ]);
    const customValidationGroupPath = useCustomValidationGroupPath(customGroup);
    const dispatch = useDispatch();
    const prev = useRef({
        isDirty: false,
        errors: []
    });
    useEffect(()=>{
        executeActions(customValidationGroupPath, dispatch, [
            ensureValidationGroupExists
        ]);
        return ()=>{
            executeActions(customValidationGroupPath, dispatch, [
                (row, path)=>removeItemFromGroup(row, path, id)
            ]);
        };
    }, [
        customValidationGroupPath,
        dispatch,
        id
    ]);
    useEffect(()=>{
        if (prev.current.isDirty !== isDirty || !isEqual(prev.current.errors, errors)) {
            executeActions(customValidationGroupPath, dispatch, [
                (row, path)=>setItemAction(row, path, id, isDirty, errors)
            ]);
            prev.current = {
                isDirty,
                errors
            };
        }
    }, [
        isDirty,
        errors,
        customValidationGroupPath,
        dispatch,
        id
    ]);
};
